import React from "react";
import {
  Box,
  Button,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import LayersClearOutlinedIcon from "@mui/icons-material/LayersClearOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const PositionsTableToolbar = ({
  theme,
  search,
  onSearchChange,
  selectedFund,
  onFundChange,
  sortedFunds,
  selectedStrategy,
  onStrategyChange,
  strategiesForSelectedFund,
  strategyFilterDisabled,
  showCloseStrategyButton,
  onCloseStrategyClick,
  onExportClick,
  positionCount,
}) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        p: 2,
        borderBottom: `1px solid ${theme.palette.divider}`,
        gap: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
        <TextField
          size="small"
          placeholder="Search positions..."
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "text.disabled" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: 300,
            "& .MuiOutlinedInput-root": {
              borderRadius: 1,
            },
          }}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Fund</InputLabel>
          <Select
            value={selectedFund}
            label="Fund"
            onChange={(e) => onFundChange(e.target.value)}
            sx={{ borderRadius: 1 }}
          >
            <MenuItem value="">All Funds</MenuItem>
            {sortedFunds.map((fund) => (
              <MenuItem key={fund.fundId} value={fund.fundId}>
                {fund.name || fund.abbreviation || fund.fundId}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 150 }} disabled={strategyFilterDisabled}>
          <InputLabel>Strategy</InputLabel>
          <Select
            value={strategyFilterDisabled ? "" : selectedStrategy}
            label="Strategy"
            onChange={(e) => onStrategyChange(e.target.value)}
            sx={{ borderRadius: 1 }}
          >
            <MenuItem value="">All Strategies</MenuItem>
            {strategiesForSelectedFund.map((row) => (
              <MenuItem key={row.strategyId} value={row.strategyId}>
                {row.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {showCloseStrategyButton ? (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={onCloseStrategyClick}
            startIcon={
              <LayersClearOutlinedIcon sx={{ fontSize: "1.125rem !important" }} />
            }
            sx={{
              height: 40,
              minHeight: 40,
              px: 2,
              borderRadius: 1,
              fontWeight: 600,
              fontSize: "0.8125rem",
              textTransform: "none",
              letterSpacing: "0.02em",
              whiteSpace: "nowrap",
              borderWidth: "1.5px !important",
              borderStyle: "solid",
              borderColor: alpha(
                theme.palette.error.main,
                theme.palette.mode === "dark" ? 0.75 : 0.65
              ),
              color: theme.palette.error.main,
              backgroundColor: "transparent",
              backgroundImage: "none",
              "& .MuiButton-startIcon": {
                color: theme.palette.error.main,
              },
              /* Theme.js forces blue gradient on all outlined buttons — override with higher specificity */
              "&&:hover": {
                backgroundImage: "none",
                backgroundColor: alpha(
                  theme.palette.error.main,
                  theme.palette.mode === "dark" ? 0.18 : 0.1
                ),
                color: theme.palette.error.main,
                borderColor: theme.palette.error.main,
                borderWidth: "1.5px !important",
                boxShadow: `0 4px 16px ${alpha(theme.palette.error.main, 0.35)}`,
                transform: "translateY(-1px)",
                "& .MuiButton-startIcon": {
                  color: theme.palette.error.main,
                },
              },
              "&&:active": {
                backgroundImage: "none",
                color: theme.palette.error.main,
                borderColor: theme.palette.error.dark,
                backgroundColor: alpha(
                  theme.palette.error.main,
                  theme.palette.mode === "dark" ? 0.26 : 0.14
                ),
                transform: "translateY(0)",
                boxShadow: `0 2px 8px ${alpha(theme.palette.error.main, 0.25)}`,
              },
              "&:focus-visible": {
                outline: `2px solid ${alpha(theme.palette.error.main, 0.5)}`,
                outlineOffset: 2,
              },
              "& .MuiTouchRipple-root .MuiTouchRipple-child": {
                backgroundColor: alpha(theme.palette.error.main, 0.28),
              },
            }}
          >
            Close strategy
          </Button>
        ) : null}
      </Box>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={onExportClick}
          startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: "1.125rem !important" }} />}
          sx={{
            height: 40,
            minHeight: 40,
            px: 2,
            borderRadius: 1,
            fontWeight: 600,
            fontSize: "0.8125rem",
            textTransform: "none",
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
          }}
        >
          Export CSV
        </Button>
        <Box sx={{ color: theme.palette.text.secondary, fontSize: "14px", whiteSpace: "nowrap" }}>
          {positionCount} positions
        </Box>
      </Box>
    </Box>
  );
};

export default PositionsTableToolbar;
